import { Controller } from 'react-hook-form';
import {
  forwardRef,
  InputHTMLAttributes,
  SelectHTMLAttributes,
  useEffect,
  useState,
} from 'react';
import { FloppyDiskBack } from 'phosphor-react';
import { PageLayout } from '../../../shared/PageLayout';
import { useRegisterForms } from './hooks/useRegisterForms';
import styles from './register.module.css';
import {
  maskCpfCnpj,
  maskPhone,
  numberOnly,
} from '../../../services/helpers/mask';
import { useRegisterData } from './hooks/useRegisterData';
import { FormValues } from './interface';
import { useAuth } from '../../../hooks/useAuth';
import { ProfileImage } from './components/ProfileImage';
import { FeedbackModal } from '../../../shared/FeedbackModal';

interface InputComponentProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
}
interface SelectComponentProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: {
    name: string;
    value: string;
  }[];
}

const InputComponent = forwardRef<HTMLDivElement, InputComponentProps>(
  ({ label, hint, ...rest }, ref) => {
    return (
      <div
        className={`${styles['input-controller']} ${hint ? styles.error : ''}`}
        ref={ref}
      >
        <span>{label}</span>
        <input {...rest} />
        {hint && <i>{hint}</i>}
      </div>
    );
  },
);
const SelectComponent = forwardRef<HTMLDivElement, SelectComponentProps>(
  ({ label, options, ...rest }, ref) => {
    return (
      <div className={styles['input-controller']} ref={ref}>
        <span>{label}</span>
        <select {...rest}>
          {options &&
            options.map(option => (
              <option key={option.value} value={option.value}>
                {option.name}
              </option>
            ))}
        </select>
      </div>
    );
  },
);

export const Register = () => {
  const [accessLabel, setAccessLabel] = useState('');
  const { workshop: data } = useAuth();

  useEffect(() => {
    const fetchWorkshopData = async () => {
      try {
        if (data && data.user) {
          if (data.user.role_name === 'gas_station') {
            setAccessLabel('do Posto');
          } else if (data.user.role_name === 'fleet') {
            setAccessLabel('da Frota');
          } else {
            setAccessLabel('da Oficina');
          }
        } else {
          setAccessLabel('da Oficina');
        }
      } catch (error) {
        setAccessLabel('da Oficina');
      }
    };

    fetchWorkshopData();
  }, [data]);

  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
    handleSubmit,
  } = useRegisterForms();
  const {
    fetchApiWorkshop,
    fetchAddressByZipCode,
    updateWorkshopData,
    updateWorkshopPassword,
    updateCode,
    isFetchingData,
  } = useRegisterData();

  const { setWorkshop } = useAuth();

  useEffect(() => {
    fetchApiWorkshop(reset);
  }, []);

  useEffect(() => {
    fetchApiWorkshop(reset);
  }, []);

  const [showPassword, setShowPassword] = useState(false);

  const handleAddress = (zipCode: string) => {
    const previousFormValues = getValues();
    fetchAddressByZipCode(zipCode, reset, previousFormValues);
  };

  const handleSaveData = async (formValues: FormValues) => {
    const res = await updateWorkshopData(formValues);
    if (
      formValues.password.trim().length > 0 ||
      formValues.confirmPassword.trim().length > 0
    ) {
      await updateWorkshopPassword(formValues);
    }
    if (formValues.code && formValues.code.trim().length > 0) {
      await updateCode(formValues);
    }

    if (res) {
      setWorkshop(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          fantasy_name: res?.fantasy_name ?? null,
          phone: res?.phone ?? null,
          whatsapp: res?.whatsapp ?? null,
          email: res?.email ?? null,
          state_registration: `${res?.state_registration ?? "'"}`,
          quotation_segment: res?.quotation_segment ?? null,
          type: res?.type ?? null,
          profile_image: res?.profile_image ?? null,
          address: {
            ...prev.address,
            zip: res?.address.zip ?? null,
            street: res?.address.street ?? null,
            uf: res?.address.uf ?? null,
            city: res?.address.city ?? null,
            district: res?.address.district ?? null,
            number: res?.address.number ?? null,
          },
        };
      });
    }
  };

  return (
    <PageLayout.Root title="Meu Cadastro">
      <FeedbackModal variant="sending" isOpen={isFetchingData} />
      <form
        className={styles.container}
        onSubmit={handleSubmit(handleSaveData)}
      >
        <section className={styles.section}>
          <h1>Dados {accessLabel}</h1>
          <div className={`${styles.row} ${styles['profile-image-container']}`}>
            <div className={styles['profile-image']}>
              <Controller
                control={control}
                name="profile_image"
                render={({ field }) => {
                  return (
                    <ProfileImage
                      onUploadComplete={imgUrl =>
                        setValue('profile_image', imgUrl)
                      }
                      imgUrl={field.value}
                      {...field}
                    />
                  );
                }}
              />
            </div>
            <div className={styles.column}>
              <Controller
                control={control}
                name="corporateName"
                render={({ field }) => {
                  return (
                    <InputComponent
                      label="Razão Social"
                      hint={
                        errors.corporateName ? errors.corporateName.message : ''
                      }
                      {...field}
                      disabled
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="fantasyName"
                render={({ field }) => {
                  return (
                    <InputComponent
                      label="Nome Fantasia"
                      hint={
                        errors.fantasyName ? errors.fantasyName.message : ''
                      }
                      {...field}
                    />
                  );
                }}
              />

              <Controller
                control={control}
                name="phone"
                render={({ field: { onChange, ...rest } }) => {
                  return (
                    <InputComponent
                      label="Telefone"
                      {...rest}
                      onChange={e => onChange(maskPhone(e.target.value))}
                      maxLength={15}
                      hint={errors.phone ? errors.phone.message : ''}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="whatsapp"
                render={({ field: { onChange, ...rest } }) => {
                  return (
                    <InputComponent
                      label="Whatsapp"
                      {...rest}
                      onChange={e => onChange(maskPhone(e.target.value))}
                      maxLength={15}
                      hint={errors.whatsapp ? errors.whatsapp.message : ''}
                    />
                  );
                }}
              />
            </div>
          </div>
          {/* <div className={styles.row}>
            <Controller
              control={control}
              name="phone"
              render={({ field: { onChange, ...rest } }) => {
                return (
                  <InputComponent
                    label="Telefone"
                    {...rest}
                    onChange={e => onChange(maskPhone(e.target.value))}
                    maxLength={15}
                    hint={errors.phone ? errors.phone.message : ''}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="whatsapp"
              render={({ field: { onChange, ...rest } }) => {
                return (
                  <InputComponent
                    label="Whatsapp"
                    {...rest}
                    onChange={e => onChange(maskPhone(e.target.value))}
                    maxLength={15}
                    hint={errors.whatsapp ? errors.whatsapp.message : ''}
                  />
                );
              }}
            />
          </div> */}
          <div className={styles.row}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => {
                return (
                  <InputComponent
                    label="E-mail"
                    {...field}
                    hint={errors.email ? errors.email.message : ''}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="document"
              render={({ field: { onChange, ...rest } }) => {
                return (
                  <InputComponent
                    label="CPF/CNPJ"
                    {...rest}
                    onChange={e => onChange(maskCpfCnpj(e.target.value))}
                    disabled
                    hint={errors.document ? errors.document.message : ''}
                  />
                );
              }}
            />
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name="stateRegistration"
              render={({ field }) => {
                return <InputComponent label="Inscrição Estadual" {...field} />;
              }}
            />

            <Controller
              control={control}
              name="type"
              render={({ field }) => {
                return (
                  <SelectComponent
                    label="Tipo de Oficina no Mapa"
                    options={[
                      {
                        value: 'CAR',
                        name: 'Carro',
                      },
                      {
                        value: 'MOTORCYCLE',
                        name: 'Moto',
                      },
                      {
                        value: 'TRUCK',
                        name: 'Caminhão',
                      },
                      {
                        value: 'ALL',
                        name: 'Carro/ Caminhão/ Moto',
                      },
                    ]}
                    {...field}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="quotation_segment"
              render={({ field }) => {
                return (
                  <SelectComponent
                    label="Participação na Cotação"
                    options={[
                      {
                        value: 'seller',
                        name: 'Vendedor',
                      },
                      {
                        value: 'buyer',
                        name: 'Comprador',
                      },
                      {
                        value: 'both',
                        name: 'Ambos',
                      },
                    ]}
                    {...field}
                  />
                );
              }}
            />
          </div>
        </section>

        <section className={styles.section}>
          <h1>Endereço</h1>
          <div className={styles.row}>
            <Controller
              control={control}
              name="address.zip"
              render={({ field: { onChange, ...rest } }) => {
                return (
                  <InputComponent
                    label="CEP"
                    {...rest}
                    onChange={e => {
                      const newValue = numberOnly(e.target.value);
                      onChange(newValue);
                      if (newValue.length >= 8) handleAddress(newValue);
                    }}
                    maxLength={9}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="address.street"
              render={({ field }) => {
                return <InputComponent label="Endereço" {...field} />;
              }}
            />
            <Controller
              control={control}
              name="address.number"
              render={({ field }) => {
                return <InputComponent label="Número" {...field} />;
              }}
            />
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name="address.district"
              render={({ field }) => {
                return <InputComponent label="Bairro" {...field} />;
              }}
            />
            <Controller
              control={control}
              name="address.city"
              render={({ field }) => {
                return <InputComponent label="Cidade" {...field} />;
              }}
            />
            <Controller
              control={control}
              name="address.uf"
              render={({ field }) => {
                return <InputComponent label="UF" {...field} />;
              }}
            />
          </div>
        </section>

        <section className={styles.section}>
          <h1>Informações de Acesso</h1>
          <div>
            <div className={styles.row}>
              <Controller
                control={control}
                name="password"
                render={({ field }) => {
                  return (
                    <InputComponent
                      label="Nova Senha (opcional)"
                      {...field}
                      hint={errors.password ? errors.password.message : ''}
                      type={showPassword ? 'text' : 'password'}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="confirmPassword"
                render={({ field }) => {
                  return (
                    <InputComponent
                      label="Confirmar Senha"
                      {...field}
                      hint={
                        errors.confirmPassword
                          ? errors.confirmPassword.message
                          : ''
                      }
                      type={showPassword ? 'text' : 'password'}
                    />
                  );
                }}
              />
            </div>
            <label
              htmlFor="showPassword"
              className={styles['show-password-controller']}
            >
              <input
                id="showPassword"
                type="checkbox"
                onChange={() => setShowPassword(previous => !previous)}
              />
              exibir senha
            </label>
          </div>
          <div className={styles.row}>
            <Controller
              control={control}
              name="code"
              render={({ field }) => {
                return <InputComponent label="Código Promocional" {...field} />;
              }}
            />
          </div>
        </section>

        <button
          type="submit"
          className={styles['save-btn']}
          // onClick={handleSaveData}
        >
          <FloppyDiskBack size={32} /> Salvar
        </button>
      </form>
    </PageLayout.Root>
  );
};
